import { useCallback, useEffect, useState } from 'react';
import type { McpConfigResponse } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';

interface UseMcpConfigResult {
  config: McpConfigResponse | null;
  loading: boolean;
  /**
   * True only once the config has resolved AND reports `enabled: true`. The
   * discoverability surface gates its render on this, so it stays dark while
   * loading, on any fetch error, or while the user is unauthenticated.
   */
  enabled: boolean;
}

/**
 * Fetches the dark-until-provisioned MCP config (`GET /api/mcp/config`, typed
 * `McpConfigResponse`) via `useApi` and exposes it to the discoverability
 * surface. Mirrors the payments dark-until-provisioned consumption: until an
 * operator provisions the MCP Auth0 audience + Cloud Run secret the endpoint
 * reports `enabled: false`, so the portal never advertises the integration
 * before the server is live (R-CONFIG-GATE). Any error / loading state is
 * treated as "not enabled" so a failed fetch keeps the surface dark.
 */
export function useMcpConfig(): UseMcpConfigResult {
  const { get, isAuthenticated } = useApi();
  const [config, setConfig] = useState<McpConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Wrapped in useCallback (mirrors PaymentMethodsPage.loadAll) so the fetch and
  // its setState calls live outside the effect body.
  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const cfg = await get<McpConfigResponse>(`${API_BASE_URL}/api/mcp/config`);
      setConfig(cfg);
    } catch {
      setConfig(null); // keep the surface dark on error
    } finally {
      setLoading(false);
    }
  }, [get, isAuthenticated]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    load();
  }, [load]);

  return { config, loading, enabled: !loading && config?.enabled === true };
}
